import { autoinject, bindable, computedFrom, observable } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { NotificationType, ResourcedNotificationService } from '../../components/notifications/notification-service';
import { OrganizationalNodeModel, OrganizationalUnitService } from '../customers/organizational-units/organizational-unit-service';
import { FinancialEntryService, IFinancialEntryDataAM } from './financial-entry-api';
import { AccountGroupListItem, FinancialEntryListItem } from './financial-entry-list';

@autoinject
export class FinancialEntryBrowser {
	@bindable customerId: string;
	@bindable fiscalYearId: string;
	@bindable scopeId: string;
	@bindable organizationId: string;
	@bindable scopeName: string;

	@bindable organizationalStructureNodes: OrganizationalNodeModel[] = null;
	@bindable selectedNode: OrganizationalNodeModel;

	@bindable summaryGroupList: FinancialEntryListItem[] = [];
	@bindable accountGroupList: AccountGroupListItem[] = [];
	@bindable accountGroupListContainer: HTMLElement;

	@bindable loadingOrganizationalStructure = false;

	@observable financialEntrySourceFilter: string;

	financialEntryData: IFinancialEntryDataAM[] = [];
	loadingFinancialEntries = false;

	private customerItemName = 'customer';
	private scopeItemName = 'scope';

	constructor(private financialEntryService: FinancialEntryService, private ea: EventAggregator, private notificationService: ResourcedNotificationService) {
	}

	customerIdChanged(newValue: string, oldValue: string) {
		this.selectedNode = null;
		this.refreshComponent();
	}

	scopeIdChanged(newValue: string, oldValue: string) {
		this.refreshComponent();
	}

	refreshComponent() {
		this.financialEntryData = null;

		if (this.customerId && this.scopeId && this.costCenterSelected) {
			this.selectedNodeChanged();
		}
	}

	financialEntrySourceFilterChanged(newValue: string) {
		this.refreshComponent();
	}

	@computedFrom('selectedNode', 'financialEntries', 'loadingFinancialEntries')
	get financialEntriesVisible(): boolean {
		return this.costCenterSelected
			&& this.financialEntryData != null
			&& !this.loadingFinancialEntries;
	}

	@computedFrom('selectedNode')
	get costCenterSelected(): boolean {
		return this.selectedNode && this.selectedNode.nodeType === 'CostCenter';
	}
	
	selectedNodeChanged() {
		if (!this.selectedNode) {
			this.financialEntryData = null;
			return;
		}

		switch (this.selectedNode.nodeType) {
			case 'CostCenter':
				this.loadingFinancialEntries = true;
				this.financialEntryService
					.getFinancialEntriesOfCostCenter(this.scopeId, this.selectedNode.id, this.financialEntrySourceFilter)
					.then(financialEntryData => {
						this.financialEntryData = financialEntryData;
						this.loadingFinancialEntries = false;
					})
					.catch(() => {
						this.loadingFinancialEntries = false;
					});
				break;
			default:
				break;
		}
	}

	copyToClipboard() {
		if (!this.selectedNode) {
			return;
		}

		const detailElements = document.querySelectorAll<HTMLDetailsElement>('details');
		detailElements.forEach(detailElement => { detailElement.open = true; });
		this.accountGroupList.forEach(accountGroup => { accountGroup.accountListVisible = true; });

		const sel = window.getSelection();
		sel.selectAllChildren(this.accountGroupListContainer);
		setTimeout(() => {
			document.execCommand('copy');
			sel.removeAllRanges();
		});

		this.notificationService.push("FinancialEntryList_Button_CopiedToClipboard", NotificationType.Success);
	}

	focusTreeView() {
		this.ea.publish('focus-tree-view');
	}
}
